import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import styles from "./index.module.scss";
import moment from "moment";
import { formatAbbreviatedCurrency } from "../../../../utils/utils";
import NoDataFoundC from "../../../common/no-data-found";
import SkeletonC from "../../../common/skeleton";
import { useSearchParams } from "react-router-dom";

type PreRow = {
  id: string | number;
  street: string;
  city: string;
  state: string;
  postalCode?: string;
  postalcode?: string;
  salePrice?: number;
  askingPrice?: number;
  purchasePrice?: number;
  purchaseDate?: string; // ISO string
  saleDate?: string; // ISO string
  currentSaleRecordingDate?: string; // yyyymmdd
  prevSaleRecordingDate?: string; // yyyymmdd
  totalSold?: string; // "0" or "1" etc.
  totalPurchased?: string; // "0" or "1" etc.
  acctName?: string;
  latitude?: number;
  longitude?: number;
};

const parseYMD = (ymd?: string) => {
  if (!ymd) return undefined;
  if (/^\d{8}$/.test(ymd)) {
    const y = ymd.slice(0, 4);
    const m = ymd.slice(4, 6);
    const d = ymd.slice(6, 8);
    return `${y}-${m}-${d}`;
  }
  return ymd;
};

const formatDate = (val?: string) => {
  if (!val) return "-";
  return moment(val).format("DD MMM YYYY");
};

const AddressCell = (row: PreRow) => {
  const postal = row.postalCode || row.postalcode || "";
  return (
    <div className={styles.tableCell}>
      <div>{row.street}</div>
      <div>{`${row.city}, ${row.state} ${postal}`}</div>
    </div>
  );
};

const PriceCell = (value?: number) => (
  <div className={`${styles.tableCell} ${styles.price}`}>
    {typeof value === "number" ? formatAbbreviatedCurrency(value, "USD") : "-"}
  </div>
);

const DateCell = (val?: string) => (
  <div className={styles.tableCell} style={{ whiteSpace: "pre-line" }}>{formatDate(val)}</div>
);

type PreListTableProps = {
  data?: PreRow[];
  isLoading?: boolean;
  isPlaceholderData?: boolean;
  hasData?: boolean;
};

const PreListSkeleton = () => (
  <div className={styles.preListSkeleton}>
    {Array.from({ length: 8 }).map((_, idx) => (
      <div key={idx} className={styles.skeletonRow}>
        <SkeletonC width="200px" height="20px" className={styles.skeletonAddress} />
        <SkeletonC width="100px" height="20px" className={styles.skeletonPrice} />
        <SkeletonC width="100px" height="20px" className={styles.skeletonPrice} />
        <SkeletonC width="120px" height="20px" className={styles.skeletonDate} />
        <SkeletonC width="120px" height="20px" className={styles.skeletonDate} />
      </div>
    ))}
  </div>
);

const PreListTable: React.FC<PreListTableProps> = ({ 
  data = [],
  isLoading = false, 
  isPlaceholderData = false, 
  hasData = true 
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get current sort order from URL params, default to DESC
  const currentSortOrder = searchParams.get("sort") || "DESC";
  
  const handleSortToggle = () => {
    const newSortOrder = currentSortOrder === "ASC" ? "DESC" : "ASC";
    setSearchParams(prev => {
      prev.set("sort", newSortOrder);
      return prev;
    });
  };

  const AddressHeader = () => (
    <div className={styles.addressHeader}>
      <span>Address</span>
      <img 
        src="/images/sort-icon.svg" 
        alt="sort" 
        onClick={handleSortToggle}
        className={`${styles.sortIcon} ${currentSortOrder === "ASC" ? styles.asc : styles.desc}`}
      />
    </div>
  );

  // Show skeleton on first load
  if (isLoading && !hasData) {
    return <PreListSkeleton />;
  }

  // Show no data if no data available
  if (!isLoading && !hasData) {
    return <NoDataFoundC panelIndex={3} properties={true} />;
  }

  return (
    <div className={`${isPlaceholderData ? styles.isPlaceholderData : ""}`}>
      <DataTable value={data} className={styles.tableClass}>
        <Column header={<AddressHeader />} body={(e) => AddressCell(e as PreRow)} headerClassName={styles.headerLeft} />
        <Column header="Sale Price" body={(e: PreRow) => PriceCell(e.salePrice ?? e.askingPrice)} headerClassName={styles.headerRight} bodyClassName={styles.cellRight} />
        <Column header="Purchase Price" body={(e: PreRow) => PriceCell(e.purchasePrice)} headerClassName={styles.headerRight} bodyClassName={styles.cellRight} />
        <Column header="Purchase Date" body={(e: PreRow) => DateCell(parseYMD(e.prevSaleRecordingDate) ?? e.purchaseDate)} headerClassName={styles.headerRight} bodyClassName={styles.cellRight} />
        <Column header="Sale Date" body={(e: PreRow) => DateCell(parseYMD(e.currentSaleRecordingDate) ?? e.saleDate)} headerClassName={styles.headerRight} bodyClassName={styles.cellRight} />
      </DataTable>
    </div>
  );
};

export default PreListTable;


